import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  listSubscriptions, subscriptionsDashboard, subscriptionsUpcoming,
  createSubscription, pauseSubscription, resumeSubscription, cancelSubscription,
  reactivateSubscription, generateInvoiceNow, subscriptionsTick,
  subscriptionEvents, subscriptionInvoices,
  listSepaMandates, createSepaMandate, listSepaBatches, generateSepaBatch, sepaBatchDownloadUrl,
  getClients,
} from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  Repeat, Plus, Loader2, TrendingUp, AlertTriangle,
  Pause, Play, X, RefreshCw, Send, Download, FileText,
  Building2, Eye, Sparkles, CheckCircle2, Clock, HelpCircle,
  Search, Filter, Users, X as XIcon,
} from 'lucide-react';

const fmt = (n) => new Intl.NumberFormat('es-ES', { useGrouping: 'always', style: 'currency', currency: 'EUR' }).format(n || 0);
const fmtDate = (s) => { try { return new Date(s).toLocaleDateString('es-ES'); } catch { return s || '—'; } };

const FREQ_LABEL = {
  weekly: 'Semanal', biweekly: 'Quincenal', monthly: 'Mensual', bimonthly: 'Bimensual',
  quarterly: 'Trimestral', biannual: 'Semestral', yearly: 'Anual', custom: 'Personalizada'
};

const STATUS_CLS = {
  active: 'bg-emerald-100 text-emerald-700',
  trial: 'bg-blue-100 text-blue-700',
  paused: 'bg-amber-100 text-amber-700',
  cancelled: 'bg-stone-100 text-stone-500',
  completed: 'bg-stone-100 text-stone-500',
  past_due: 'bg-rose-100 text-rose-700',
};
const STATUS_LABEL = { active: 'Activa', trial: 'Trial', paused: 'Pausada', cancelled: 'Cancelada', completed: 'Completada', past_due: 'Impago' };

const STATUS_CHIPS = [
  { value: 'all', label: 'Todas', dot: 'bg-stone-400' },
  { value: 'active', label: 'Activas', dot: 'bg-emerald-500' },
  { value: 'trial', label: 'Trial', dot: 'bg-blue-500' },
  { value: 'paused', label: 'Pausadas', dot: 'bg-amber-500' },
  { value: 'past_due', label: 'Impago', dot: 'bg-rose-500' },
  { value: 'cancelled', label: 'Canceladas', dot: 'bg-stone-400' },
];

const FREQ_CHIPS = [
  { value: 'all', label: 'Todas' },
  { value: 'monthly', label: 'Mensual' },
  { value: 'quarterly', label: 'Trimestral' },
  { value: 'biannual', label: 'Semestral' },
  { value: 'yearly', label: 'Anual' },
  { value: 'weekly', label: 'Semanal' },
];

const StatCard = ({ label, value, sub, icon: Icon, gradient, iconBg, testid }) => (
  <div
    data-testid={testid}
    className={`relative overflow-hidden rounded-2xl p-5 border border-white/40 shadow-[0_8px_24px_rgb(0,0,0,0.06)] hover:shadow-[0_12px_30px_rgb(0,0,0,0.08)] hover:-translate-y-0.5 transition-all duration-300 ${gradient}`}
  >
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-stone-500 mb-1">{label}</p>
        <p className="text-2xl font-extrabold text-stone-900 tracking-tight font-heading truncate">{value}</p>
        {sub && <p className="text-xs text-stone-500 mt-1">{sub}</p>}
      </div>
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${iconBg}`}>
        <Icon className="w-5 h-5 text-white" strokeWidth={2} />
      </div>
    </div>
  </div>
);

const FilterChip = ({ active, onClick, children, testid, dot }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className={`relative inline-flex items-center gap-2 px-3.5 py-2 rounded-xl text-xs font-semibold border transition-all duration-200 whitespace-nowrap ${
      active
        ? 'bg-[#FF6B4A] text-white border-[#FF6B4A] shadow-md shadow-orange-200/60 -translate-y-0.5'
        : 'bg-white border-stone-200 text-stone-700 hover:border-orange-300 hover:bg-orange-50/40 shadow-sm'
    }`}
  >
    {dot && <span className={`inline-block w-1.5 h-1.5 rounded-full ${active ? 'bg-white/80' : dot}`} />}
    {children}
  </button>
);

const SubscriptionsPage = () => {
  const [loading, setLoading] = useState(true);
  const [dash, setDash] = useState(null);
  const [subs, setSubs] = useState([]);
  const [upcoming, setUpcoming] = useState([]);
  const [mandates, setMandates] = useState([]);
  const [batches, setBatches] = useState([]);
  const [clients, setClients] = useState([]);
  const [filter, setFilter] = useState('all');
  const [freqFilter, setFreqFilter] = useState('all');
  const [clientFilter, setClientFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [tab, setTab] = useState('subs');
  const [showCreate, setShowCreate] = useState(false);
  const [showMandate, setShowMandate] = useState(false);
  const [detailSub, setDetailSub] = useState(null);
  const [running, setRunning] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  const load = useCallback(async () => {
    try {
      const [d, sl, up, m, b, c] = await Promise.all([
        subscriptionsDashboard(),
        listSubscriptions({}),
        subscriptionsUpcoming(14),
        listSepaMandates(),
        listSepaBatches(),
        getClients(),
      ]);
      setDash(d.data);
      setSubs(sl.data?.items || []);
      setUpcoming(up.data?.items || []);
      setMandates(m.data?.items || []);
      setBatches(b.data?.items || []);
      setClients(c.data || []);
    } catch (e) {
      toast.error('Error cargando suscripciones');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const subClients = useMemo(() => {
    const set = new Set(subs.map((s) => s.client_name).filter(Boolean));
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'es'));
  }, [subs]);

  const filteredSubs = useMemo(() => {
    const q = searchTerm.toLowerCase().trim();
    return subs.filter((s) => {
      if (filter !== 'all' && s.status !== filter) return false;
      if (freqFilter !== 'all' && s.frequency_type !== freqFilter) return false;
      if (clientFilter !== 'all' && s.client_name !== clientFilter) return false;
      if (q) {
        const hay = `${s.name || ''} ${s.client_name || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [subs, filter, freqFilter, clientFilter, searchTerm]);

  const filteredStats = useMemo(() => {
    let mrr = 0;
    let activeCount = 0;
    let pastDueCount = 0;
    filteredSubs.forEach((s) => {
      if (s.status === 'active' || s.status === 'trial') {
        mrr += Number(s.mrr_normalized) || 0;
        activeCount += 1;
      }
      if (s.status === 'past_due') pastDueCount += 1;
    });
    return { mrr, arr: mrr * 12, activeCount, pastDueCount, total: filteredSubs.length };
  }, [filteredSubs]);

  const activeFiltersCount = [
    filter !== 'all',
    freqFilter !== 'all',
    clientFilter !== 'all',
    searchTerm.trim() !== '',
  ].filter(Boolean).length;

  const resetFilters = () => {
    setFilter('all'); setFreqFilter('all'); setClientFilter('all'); setSearchTerm('');
  };

  const handleTick = async () => {
    setRunning(true);
    try {
      const r = await subscriptionsTick();
      toast.success(`Procesadas: ${r.data.processed} · Facturas: ${r.data.generated}`);
      load();
    } catch (e) {
      toast.error('Error en tick');
    } finally {
      setRunning(false);
    }
  };

  const handleGenerateBatch = async () => {
    try {
      const r = await generateSepaBatch();
      if (r.data.status === 'noop' || r.data.transaction_count === 0) {
        toast.info(r.data.message || 'No hay facturas SEPA pendientes para batchear');
      } else {
        toast.success(`Lote generado · ${r.data.transaction_count} facturas · ${fmt(r.data.total_amount)}`);
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo generar el lote');
    }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-[#FF6B4A]" /></div>;

  return (
    <div className="space-y-6" data-testid="subscriptions-page">
      <div className="flex justify-between items-start flex-wrap gap-3">
        <div>
          <h1 className="text-3xl sm:text-4xl font-heading font-extrabold text-stone-900 flex items-center gap-3">
            <Repeat className="w-8 h-8 text-[#FF6B4A]" />
            Suscripciones
          </h1>
          <p className="text-sm text-stone-500 mt-1">Facturación recurrente · MRR · SEPA · Ciclo de vida</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            variant="outline"
            onClick={() => setShowHelp(true)}
            className="rounded-full border-violet-200 text-violet-700 hover:bg-violet-50"
            data-testid="subs-help-btn"
          >
            <HelpCircle className="w-4 h-4 mr-2" />
            ¿Qué es esto?
          </Button>
          <Button variant="outline" onClick={handleTick} disabled={running} data-testid="subs-tick-btn">
            {running ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
            Generar facturas
          </Button>
          <Button onClick={() => setShowCreate(true)} className="bg-[#FF6B4A] text-white" data-testid="new-subscription-btn">
            <Plus className="w-4 h-4 mr-2" /> Nueva suscripción
          </Button>
        </div>
      </div>

      {/* Metrics — visual stat cards (orange/teal/blue/rose) */}
      {dash && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4" data-testid="subs-metrics">
          <StatCard
            label="MRR"
            value={fmt(filteredStats.mrr || dash.mrr)}
            sub={`${filteredStats.activeCount} activa${filteredStats.activeCount === 1 ? '' : 's'}`}
            icon={TrendingUp}
            gradient="bg-gradient-to-br from-orange-50 to-amber-50"
            iconBg="bg-gradient-to-br from-[#FF6B4A] to-[#FBBF24]"
            testid="stat-mrr"
          />
          <StatCard
            label="ARR"
            value={fmt(filteredStats.arr || dash.arr)}
            sub="MRR × 12"
            icon={Sparkles}
            gradient="bg-gradient-to-br from-amber-50 to-yellow-50"
            iconBg="bg-gradient-to-br from-amber-500 to-yellow-500"
            testid="stat-arr"
          />
          <StatCard
            label="Activas"
            value={dash.active_count}
            sub={`Total: ${dash.active_count + (dash.past_due_count || 0)}`}
            icon={CheckCircle2}
            gradient="bg-gradient-to-br from-emerald-50 to-teal-50"
            iconBg="bg-gradient-to-br from-emerald-500 to-teal-500"
            testid="stat-active"
          />
          <StatCard
            label="Nuevas mes"
            value={dash.new_this_month}
            sub="Altas mes en curso"
            icon={Plus}
            gradient="bg-gradient-to-br from-sky-50 to-blue-50"
            iconBg="bg-gradient-to-br from-sky-500 to-blue-500"
            testid="stat-new"
          />
          <StatCard
            label="Impago"
            value={dash.past_due_count}
            sub="Cobros fallidos"
            icon={AlertTriangle}
            gradient="bg-gradient-to-br from-rose-50 to-red-50"
            iconBg="bg-gradient-to-br from-rose-500 to-red-500"
            testid="stat-past-due"
          />
        </div>
      )}

      {/* Tab nav (pill style) */}
      <div className="flex gap-1 bg-white rounded-2xl border border-stone-100 shadow-sm p-1 w-fit">
        {[{ k: 'subs', l: 'Suscripciones' }, { k: 'sepa', l: 'SEPA' }, { k: 'upcoming', l: 'Próximas' }].map((t) => (
          <button key={t.k} onClick={() => setTab(t.k)}
            className={`px-4 py-2 text-sm font-semibold rounded-xl transition ${tab === t.k ? 'bg-gradient-to-r from-[#FF6B4A] to-[#FBBF24] text-white shadow-md shadow-orange-200/40' : 'text-stone-600 hover:bg-stone-50'}`}
            data-testid={`subs-tab-${t.k}`}>
            {t.l}
          </button>
        ))}
      </div>

      {tab === 'subs' && (
        <>
          {/* Filter card with shadow */}
          <div className="bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-stone-100 p-5 space-y-4" data-testid="subs-filters-card">
            {/* Search + client + reset */}
            <div className="flex flex-col lg:flex-row gap-3">
              <div className="relative flex-1 min-w-0">
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400 pointer-events-none" />
                <Input
                  placeholder="Buscar por nombre o cliente…"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-10 h-11 rounded-xl bg-stone-50 border-stone-200 focus:bg-white"
                  data-testid="subs-search-input"
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 rounded-full bg-stone-200 hover:bg-stone-300 flex items-center justify-center text-stone-600 transition-colors"
                    data-testid="subs-clear-search"
                  >
                    <XIcon className="w-3 h-3" />
                  </button>
                )}
              </div>
              <div className="relative lg:w-64">
                <Users className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400 pointer-events-none z-10" />
                <Select value={clientFilter} onValueChange={setClientFilter}>
                  <SelectTrigger className="pl-10 h-11 rounded-xl bg-stone-50 border-stone-200 focus:bg-white" data-testid="subs-client-filter">
                    <SelectValue placeholder="Todos los clientes" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los clientes</SelectItem>
                    {subClients.map((c) => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {activeFiltersCount > 0 && (
                <button
                  onClick={resetFilters}
                  className="inline-flex items-center gap-2 px-4 h-11 rounded-xl text-sm font-semibold text-stone-600 bg-stone-50 hover:bg-stone-100 border border-stone-200 transition-all whitespace-nowrap"
                  data-testid="subs-reset-filters"
                >
                  <XIcon className="w-3.5 h-3.5" /> Limpiar ({activeFiltersCount})
                </button>
              )}
            </div>

            {/* Status chips */}
            <div className="flex items-center gap-3 flex-wrap">
              <span className="text-[10px] font-bold uppercase tracking-widest text-stone-400 flex items-center gap-1.5">
                <Filter className="w-3 h-3" /> Estado
              </span>
              <div className="flex gap-2 flex-wrap" data-testid="subs-status-chips">
                {STATUS_CHIPS.map((s) => (
                  <FilterChip
                    key={s.value}
                    active={filter === s.value}
                    onClick={() => setFilter(s.value)}
                    dot={s.dot}
                    testid={`subs-filter-${s.value}`}
                  >
                    {s.label}
                  </FilterChip>
                ))}
              </div>
            </div>

            {/* Frequency chips */}
            <div className="flex items-center gap-3 flex-wrap">
              <span className="text-[10px] font-bold uppercase tracking-widest text-stone-400 flex items-center gap-1.5">
                <Filter className="w-3 h-3" /> Frecuencia
              </span>
              <div className="flex gap-2 flex-wrap" data-testid="subs-freq-chips">
                {FREQ_CHIPS.map((f) => (
                  <FilterChip
                    key={f.value}
                    active={freqFilter === f.value}
                    onClick={() => setFreqFilter(f.value)}
                    testid={`subs-freq-${f.value}`}
                  >
                    {f.label}
                  </FilterChip>
                ))}
              </div>
            </div>
          </div>

          {/* Table card with shadow */}
          <div className="bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-stone-100 overflow-hidden">
            {filteredSubs.length === 0 ? (
              <div className="p-12 text-center text-stone-500" data-testid="subs-empty">
                <Repeat className="w-12 h-12 mx-auto text-stone-300 mb-3" />
                <p className="font-medium">
                  {activeFiltersCount > 0 ? 'Sin resultados para los filtros aplicados' : 'Aún no tienes suscripciones'}
                </p>
                {activeFiltersCount > 0 ? (
                  <button onClick={resetFilters} className="mt-3 inline-flex items-center gap-2 px-5 py-2 rounded-full bg-stone-100 hover:bg-stone-200 text-stone-700 font-semibold text-sm transition-all">
                    <XIcon className="w-4 h-4" /> Limpiar filtros
                  </button>
                ) : (
                  <p className="text-xs text-stone-400 mt-1">Crea tu primera suscripción para empezar a facturar de forma recurrente.</p>
                )}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-stone-50/60 text-xs uppercase text-stone-500">
                    <tr>
                      <th className="text-left px-4 py-3">Nombre</th>
                      <th className="text-left px-4 py-3">Cliente</th>
                      <th className="text-left px-4 py-3 hidden md:table-cell">Frecuencia</th>
                      <th className="text-right px-4 py-3">MRR</th>
                      <th className="text-left px-4 py-3 hidden md:table-cell">Próxima</th>
                      <th className="text-center px-4 py-3">Estado</th>
                      <th className="px-4 py-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredSubs.map((s) => (
                      <tr key={s.id} className="border-t border-stone-100 hover:bg-orange-50/30 transition-colors" data-testid={`sub-row-${s.id}`}>
                        <td className="px-4 py-4 font-semibold text-stone-900">{s.name || <span className="italic text-stone-400">Sin nombre</span>}</td>
                        <td className="px-4 py-4 text-stone-600">{s.client_name}</td>
                        <td className="px-4 py-4 hidden md:table-cell">{FREQ_LABEL[s.frequency_type]}</td>
                        <td className="px-4 py-4 text-right font-mono font-bold text-stone-900">{fmt(s.mrr_normalized)}</td>
                        <td className="px-4 py-4 text-stone-500 hidden md:table-cell">{fmtDate(s.next_billing_date)}</td>
                        <td className="px-4 py-4 text-center">
                          <Badge className={`${STATUS_CLS[s.status]} border-0`}>{STATUS_LABEL[s.status]}</Badge>
                        </td>
                        <td className="px-4 py-4 text-right">
                          <button onClick={() => setDetailSub(s)} className="text-[#FF6B4A] hover:underline text-xs font-semibold" data-testid={`view-sub-${s.id}`}>
                            <Eye className="w-3.5 h-3.5 inline" /> Ver
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {tab === 'sepa' && (
        <div className="space-y-4" data-testid="sepa-tab">
          <div className="bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-stone-100 p-5">
            <div className="flex justify-between items-center mb-3">
              <h2 className="font-bold text-stone-800 flex items-center gap-2">
                <Building2 className="w-5 h-5 text-[#FF6B4A]" /> Mandatos SEPA
              </h2>
              <Button size="sm" onClick={() => setShowMandate(true)} variant="outline" data-testid="new-mandate-btn">
                <Plus className="w-4 h-4 mr-1" /> Nuevo mandato
              </Button>
            </div>
            {mandates.length === 0 ? (
              <p className="text-sm text-stone-500 text-center py-6">Sin mandatos SEPA registrados</p>
            ) : (
              <table className="w-full text-sm">
                <thead className="text-xs uppercase text-stone-500">
                  <tr><th className="text-left py-1">UMR</th><th className="text-left py-1">Titular</th><th className="text-left py-1">IBAN</th><th className="text-center py-1">Estado</th></tr>
                </thead>
                <tbody>
                  {mandates.map((m) => (
                    <tr key={m.id} className="border-t border-stone-100">
                      <td className="py-2 font-mono text-xs">{m.umr}</td>
                      <td className="py-2">{m.holder_name}</td>
                      <td className="py-2 font-mono text-xs">{m.iban}</td>
                      <td className="py-2 text-center">
                        <Badge className={m.status === 'active' ? 'bg-emerald-100 text-emerald-700 border-0' : 'bg-stone-100 text-stone-500 border-0'}>{m.status}</Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div className="bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-stone-100 p-5">
            <div className="flex justify-between items-center mb-3">
              <h2 className="font-bold text-stone-800">Lotes SEPA pain.008</h2>
              <Button size="sm" onClick={handleGenerateBatch} className="bg-[#FF6B4A] text-white" data-testid="generate-batch-btn">
                <FileText className="w-4 h-4 mr-1" /> Generar lote
              </Button>
            </div>
            {batches.length === 0 ? (
              <p className="text-sm text-stone-500 text-center py-6">Sin lotes generados</p>
            ) : (
              <table className="w-full text-sm">
                <thead className="text-xs uppercase text-stone-500">
                  <tr><th className="text-left py-1">Fecha</th><th className="text-right py-1">Importe</th><th className="text-center py-1">Tx</th><th className="px-2 py-1"></th></tr>
                </thead>
                <tbody>
                  {batches.map((b) => (
                    <tr key={b.id} className="border-t border-stone-100">
                      <td className="py-2">{fmtDate(b.created_at)}</td>
                      <td className="py-2 text-right font-bold">{fmt(b.total_amount)}</td>
                      <td className="py-2 text-center">{b.transaction_count}</td>
                      <td className="py-2 text-right">
                        <a href={sepaBatchDownloadUrl(b.id)} target="_blank" rel="noreferrer" className="text-[#FF6B4A] hover:underline text-xs">
                          <Download className="w-3.5 h-3.5 inline mr-0.5" /> XML
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {tab === 'upcoming' && (
        <div className="bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-stone-100 overflow-hidden">
          <div className="p-4 border-b border-stone-100">
            <h2 className="font-bold text-stone-800">Próximas facturas (14 días)</h2>
          </div>
          {upcoming.length === 0 ? (
            <p className="text-center py-6 text-stone-500">Sin facturas previstas</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-stone-50 text-xs uppercase text-stone-500">
                <tr><th className="text-left px-4 py-2">Fecha</th><th className="text-left px-4 py-2">Cliente</th><th className="text-left px-4 py-2">Suscripción</th><th className="text-right px-4 py-2">Importe</th></tr>
              </thead>
              <tbody>
                {upcoming.map((u) => (
                  <tr key={u.id} className="border-t border-stone-100">
                    <td className="px-4 py-2 font-mono">{fmtDate(u.next_billing_date)}</td>
                    <td className="px-4 py-2">{u.client_name}</td>
                    <td className="px-4 py-2">{u.name}</td>
                    <td className="px-4 py-2 text-right font-bold">{fmt(u.total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* CREATE DIALOG */}
      <CreateSubscriptionDialog
        open={showCreate}
        onClose={() => setShowCreate(false)}
        clients={clients}
        mandates={mandates}
        onCreated={() => { setShowCreate(false); load(); }}
      />
      {/* MANDATE DIALOG */}
      <CreateMandateDialog
        open={showMandate}
        onClose={() => setShowMandate(false)}
        clients={clients}
        onCreated={() => { setShowMandate(false); load(); }}
      />
      {/* DETAIL DIALOG */}
      <SubscriptionDetailDialog
        sub={detailSub}
        onClose={() => setDetailSub(null)}
        onReload={() => { load(); if (detailSub) setDetailSub(null); }}
      />
      {/* HELP DIALOG */}
      <SubscriptionsHelpDialog open={showHelp} onClose={() => setShowHelp(false)} onCreate={() => { setShowHelp(false); setShowCreate(true); }} />
    </div>
  );
};

const SubscriptionsHelpDialog = ({ open, onClose, onCreate }) => (
  <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
    <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2 font-heading text-2xl">
          <Repeat className="w-6 h-6 text-[#FF6B4A]" />
          ¿Qué son las Suscripciones?
        </DialogTitle>
      </DialogHeader>

      <div className="space-y-6 mt-2 text-sm text-stone-700">
        {/* Intro */}
        <section className="bg-gradient-to-r from-orange-50 to-violet-50/60 border border-orange-100 rounded-2xl p-4">
          <p className="leading-relaxed">
            Es <strong>facturación recurrente automática</strong>. En lugar de crear una factura cada mes a mano,
            defines una vez "el cliente X paga Y € cada mes/trimestre/año" y el sistema:
          </p>
          <ul className="mt-2 space-y-1 ml-4 list-disc text-stone-600">
            <li><strong>Crea la factura automáticamente</strong> cuando toca (mes/trimestre/año)</li>
            <li><strong>Cobra automáticamente</strong> por SEPA si el cliente firmó mandato bancario</li>
            <li><strong>Lleva un control</strong> de ingresos predecibles (MRR/ARR)</li>
          </ul>
        </section>

        {/* Métricas */}
        <section>
          <h3 className="font-heading font-bold text-base text-stone-900 mb-2 flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-[#FF6B4A]" />
            Las métricas de arriba
          </h3>
          <ul className="space-y-1.5 ml-4 list-disc">
            <li><strong>MRR</strong> (Monthly Recurring Revenue): ingresos garantizados cada mes</li>
            <li><strong>ARR</strong> (Annual Recurring Revenue): MRR × 12</li>
            <li><strong>Activas</strong>: suscripciones "vivas" facturando ahora</li>
            <li><strong>Nuevas este mes</strong>: altas nuevas</li>
            <li><strong>Impago</strong>: clientes que han fallado el cobro</li>
          </ul>
        </section>

        {/* Ejemplo paso a paso */}
        <section>
          <h3 className="font-heading font-bold text-base text-stone-900 mb-3 flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-violet-600" />
            Cómo crear una suscripción — ejemplo paso a paso
          </h3>
          <p className="text-stone-600 mb-3 italic">
            Imagina que llevas el mantenimiento informático de un cliente que te paga <strong>80 € + IVA cada mes</strong>.
          </p>
          <div className="space-y-2.5">
            {[
              ['1', 'Cliente *', 'Eliges del desplegable (ej: Acme S.L.)'],
              ['2', 'Nombre (opcional)', 'Etiqueta interna ("Mantenimiento Pro"). No aparece en la factura'],
              ['3', 'Conceptos *', 'Mantenimiento informático mensual · Cant: 1 · Precio: 80 · IVA: 21'],
              ['4', 'Frecuencia', 'Semanal · Quincenal · Mensual · Bimestral · Trimestral · Semestral · Anual'],
              ['5', 'Inicio *', 'Fecha de la primera factura. Las siguientes se generan solas'],
              ['6', 'Fin (opc.)', 'Vacío = indefinido. Con contrato a 12 meses, pones fecha límite'],
              ['7', 'Ciclos totales (opc.)', 'Alternativa: "factura 12 veces y para"'],
              ['8', 'Trial hasta (opc.)', 'Si das un mes gratis de prueba, fecha en que empieza a cobrar'],
              ['9', 'Día facturación', '"1" = día 1 de cada mes. "Auto" = usa el día del inicio'],
              ['10', 'Método de cobro', 'Manual (envías factura) o SEPA (cobro automático del IBAN)'],
            ].map(([num, title, desc]) => (
              <div key={num} className="flex gap-3 items-start">
                <span className="flex-shrink-0 w-7 h-7 rounded-full bg-[#FF6B4A] text-white text-xs font-bold flex items-center justify-center">
                  {num}
                </span>
                <div className="flex-1">
                  <strong className="text-stone-900">{title}</strong>
                  <p className="text-stone-600 text-xs leading-relaxed mt-0.5">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Qué pasa después */}
        <section>
          <h3 className="font-heading font-bold text-base text-stone-900 mb-2 flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-emerald-600" />
            Qué pasa después
          </h3>
          <ol className="space-y-1.5 ml-4 list-decimal text-stone-600">
            <li>El día indicado, la <strong>factura aparece sola</strong> en Facturas</li>
            <li>Si es SEPA, se genera el <strong>fichero XML pain.001</strong> para tu banco</li>
            <li>El MRR sube en 80 € (ya sabes que tienes 80 €/mes garantizados)</li>
            <li>Si pasa la fecha y nadie pagó, aparece en la pestaña <strong>Impago</strong></li>
            <li>Puedes <strong>pausar</strong> (vacaciones) o <strong>cancelar</strong> cuando el cliente se va</li>
          </ol>
        </section>

        {/* Ejemplos típicos */}
        <section>
          <h3 className="font-heading font-bold text-base text-stone-900 mb-2 flex items-center gap-2">
            <Building2 className="w-4 h-4 text-blue-600" />
            Ejemplos típicos de uso
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {[
              ['Asesoría', 'Cuota mensual 90 € + IVA'],
              ['Mantenimiento web', '50 €/mes hosting + soporte'],
              ['Academia', '120 €/mes clases particulares'],
              ['Gimnasio', '35 €/mes cuota socio'],
              ['Limpieza', '200 €/mes oficina'],
              ['SaaS', 'Plan Pro 29 €/mes'],
            ].map(([sector, ejemplo]) => (
              <div key={sector} className="flex items-center gap-2 p-2.5 bg-stone-50 rounded-xl">
                <span className="font-semibold text-stone-800 text-xs">{sector}:</span>
                <span className="text-xs text-stone-600">{ejemplo}</span>
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <div className="flex gap-3 pt-4 border-t border-stone-100">
          <Button variant="outline" onClick={onClose} className="flex-1 rounded-xl">Cerrar</Button>
          <Button onClick={onCreate} className="flex-1 bg-[#FF6B4A] hover:bg-[#E05A3E] text-white rounded-full">
            <Plus className="w-4 h-4 mr-2" />Crear mi primera suscripción
          </Button>
        </div>
      </div>
    </DialogContent>
  </Dialog>
);

const CreateSubscriptionDialog = ({ open, onClose, clients, mandates, onCreated }) => {
  const [form, setForm] = useState({
    client_id: '', name: '', items: [{ description: '', quantity: 1, unit_price: 0, tax_rate: 21 }],
    frequency_type: 'monthly', frequency_interval: 1, billing_day: '',
    start_date: new Date().toISOString().slice(0, 10), end_date: '', total_cycles: '',
    trial_end_date: '', payment_method: 'manual', payment_method_token: '',
  });
  const [saving, setSaving] = useState(false);

  const clientMandates = mandates.filter((m) => m.client_id === form.client_id && m.status === 'active');

  const updItem = (idx, k, v) => {
    const items = [...form.items];
    items[idx] = { ...items[idx], [k]: k === 'description' ? v : parseFloat(v) || 0 };
    setForm({ ...form, items });
  };
  const addItem = () => setForm({ ...form, items: [...form.items, { description: '', quantity: 1, unit_price: 0, tax_rate: 21 }] });
  const removeItem = (idx) => setForm({ ...form, items: form.items.filter((_, i) => i !== idx) });

  const submit = async () => {
    if (!form.client_id) return toast.error('Selecciona cliente');
    if (!form.items.length || !form.items[0].description) return toast.error('Añade al menos un concepto');
    if (form.payment_method === 'sepa' && !form.payment_method_token) return toast.error('Selecciona un mandato SEPA');
    setSaving(true);
    try {
      const payload = { ...form };
      ['billing_day', 'total_cycles'].forEach((k) => { if (!payload[k]) delete payload[k]; });
      ['end_date', 'trial_end_date', 'name'].forEach((k) => { if (!payload[k]) delete payload[k]; });
      if (!payload.payment_method_token) delete payload.payment_method_token;
      await createSubscription(payload);
      toast.success('Suscripción creada');
      onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al crear');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="create-sub-dialog">
        <DialogHeader><DialogTitle>Nueva suscripción</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Cliente *</Label>
              <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
                <SelectTrigger data-testid="sub-client-select"><SelectValue placeholder="Selecciona..." /></SelectTrigger>
                <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Nombre (opcional)</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Mantenimiento Pro" />
            </div>
          </div>

          <div>
            <Label>Conceptos *</Label>
            {form.items.map((it, i) => (
              <div key={i} className="flex gap-2 mb-2">
                <Input value={it.description} onChange={(e) => updItem(i, 'description', e.target.value)} placeholder="Descripción" className="flex-1" />
                <Input type="number" value={it.quantity} onChange={(e) => updItem(i, 'quantity', e.target.value)} placeholder="Qty" className="w-16" />
                <Input type="number" step="0.01" value={it.unit_price} onChange={(e) => updItem(i, 'unit_price', e.target.value)} placeholder="€" className="w-24" />
                <Input type="number" value={it.tax_rate} onChange={(e) => updItem(i, 'tax_rate', e.target.value)} placeholder="IVA%" className="w-16" />
                <Button variant="ghost" size="sm" onClick={() => removeItem(i)} disabled={form.items.length === 1}><X className="w-4 h-4" /></Button>
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={addItem}><Plus className="w-4 h-4 mr-1" /> Añadir concepto</Button>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>Frecuencia</Label>
              <Select value={form.frequency_type} onValueChange={(v) => setForm({ ...form, frequency_type: v })}>
                <SelectTrigger data-testid="sub-freq-select"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(FREQ_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {form.frequency_type === 'custom' && (
              <div><Label>Cada N días</Label><Input type="number" value={form.frequency_interval} onChange={(e) => setForm({ ...form, frequency_interval: parseInt(e.target.value) || 1 })} /></div>
            )}
            <div><Label>Inicio *</Label><Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></div>
            <div><Label>Fin (opc.)</Label><Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} /></div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div><Label>Ciclos totales (opc.)</Label><Input type="number" value={form.total_cycles} onChange={(e) => setForm({ ...form, total_cycles: e.target.value })} placeholder="∞" /></div>
            <div><Label>Trial hasta (opc.)</Label><Input type="date" value={form.trial_end_date} onChange={(e) => setForm({ ...form, trial_end_date: e.target.value })} /></div>
            <div><Label>Día facturación</Label><Input type="number" min="1" max="31" value={form.billing_day} onChange={(e) => setForm({ ...form, billing_day: e.target.value })} placeholder="Auto" /></div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Método de cobro</Label>
              <Select value={form.payment_method} onValueChange={(v) => setForm({ ...form, payment_method: v, payment_method_token: '' })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="manual">Manual (envío factura)</SelectItem>
                  <SelectItem value="sepa">Domiciliación SEPA</SelectItem>
                  <SelectItem value="stripe">Tarjeta (Stripe)</SelectItem>
                  <SelectItem value="transfer">Transferencia</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.payment_method === 'sepa' && (
              <div>
                <Label>Mandato SEPA</Label>
                <Select value={form.payment_method_token} onValueChange={(v) => setForm({ ...form, payment_method_token: v })}>
                  <SelectTrigger data-testid="sub-mandate-select"><SelectValue placeholder={clientMandates.length ? 'Selecciona...' : 'Primero crea un mandato'} /></SelectTrigger>
                  <SelectContent>{clientMandates.map((m) => <SelectItem key={m.id} value={m.id}>{m.umr} · {m.iban.slice(-4)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t border-stone-100">
            <Button variant="outline" onClick={onClose}>Cancelar</Button>
            <Button onClick={submit} disabled={saving} className="bg-[#FF6B4A] text-white" data-testid="save-sub-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}Crear suscripción
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const CreateMandateDialog = ({ open, onClose, clients, onCreated }) => {
  const [form, setForm] = useState({ client_id: '', iban: '', holder_name: '', signature_method: 'web' });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!form.client_id || !form.iban || !form.holder_name) return toast.error('Completa todos los campos');
    setSaving(true);
    try {
      await createSepaMandate(form);
      toast.success('Mandato creado');
      onCreated();
      setForm({ client_id: '', iban: '', holder_name: '', signature_method: 'web' });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="create-mandate-dialog">
        <DialogHeader><DialogTitle>Nuevo mandato SEPA</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Cliente *</Label>
            <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
              <SelectTrigger data-testid="mandate-client-select"><SelectValue placeholder="Selecciona..." /></SelectTrigger>
              <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Titular *</Label><Input value={form.holder_name} onChange={(e) => setForm({ ...form, holder_name: e.target.value })} placeholder="Nombre del titular" /></div>
          <div><Label>IBAN *</Label><Input value={form.iban} onChange={(e) => setForm({ ...form, iban: e.target.value.toUpperCase() })} placeholder="ES91 2100 0418 4502 0005 1332" className="font-mono" data-testid="mandate-iban-input" />
            <p className="text-[10px] text-stone-400 mt-0.5">Se valida automáticamente con checksum ISO 13616</p>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose}>Cancelar</Button>
            <Button onClick={submit} disabled={saving} className="bg-[#FF6B4A] text-white" data-testid="save-mandate-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}Crear mandato
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const SubscriptionDetailDialog = ({ sub, onClose, onReload }) => {
  const [events, setEvents] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [tab, setTab] = useState('actions');

  useEffect(() => {
    if (!sub) return;
    subscriptionEvents(sub.id).then((r) => setEvents(r.data?.items || []));
    subscriptionInvoices(sub.id).then((r) => setInvoices(r.data?.items || []));
  }, [sub]);

  if (!sub) return null;

  const doAction = async (action) => {
    try {
      if (action === 'pause') await pauseSubscription(sub.id);
      else if (action === 'resume') await resumeSubscription(sub.id);
      else if (action === 'cancel-now') await cancelSubscription(sub.id, 'immediate');
      else if (action === 'cancel-end') await cancelSubscription(sub.id, 'end_of_period');
      else if (action === 'reactivate') await reactivateSubscription(sub.id);
      else if (action === 'invoice-now') await generateInvoiceNow(sub.id);
      toast.success('Acción aplicada');
      onReload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    }
  };

  return (
    <Dialog open={!!sub} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="sub-detail-dialog">
        <DialogHeader>
          <DialogTitle>{sub.name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <Field label="Cliente" value={sub.client_name} />
            <Field label="Frecuencia" value={FREQ_LABEL[sub.frequency_type]} />
            <Field label="MRR" value={fmt(sub.mrr_normalized)} />
            <Field label="Total / ciclo" value={fmt(sub.total)} />
            <Field label="Próxima factura" value={fmtDate(sub.next_billing_date)} />
            <Field label="Ciclo actual" value={`${sub.current_cycle}${sub.total_cycles ? '/' + sub.total_cycles : ''}`} />
            <Field label="Método cobro" value={sub.payment_method} />
            <Field label="Estado" value={<Badge className={STATUS_CLS[sub.status]}>{STATUS_LABEL[sub.status]}</Badge>} />
          </div>

          <div className="flex gap-1 border-b border-stone-100">
            {['actions', 'invoices', 'events'].map((t) => (
              <button key={t} onClick={() => setTab(t)}
                className={`px-3 py-2 text-sm font-medium border-b-2 ${tab === t ? 'border-[#FF6B4A] text-stone-900' : 'border-transparent text-stone-500'}`}
                data-testid={`detail-tab-${t}`}>
                {t === 'actions' ? 'Acciones' : t === 'invoices' ? `Facturas (${invoices.length})` : `Eventos (${events.length})`}
              </button>
            ))}
          </div>

          {tab === 'actions' && (
            <div className="flex flex-wrap gap-2">
              {sub.status === 'active' && (
                <>
                  <Button size="sm" variant="outline" onClick={() => doAction('pause')} data-testid="sub-action-pause"><Pause className="w-4 h-4 mr-1" /> Pausar</Button>
                  <Button size="sm" variant="outline" onClick={() => doAction('invoice-now')} data-testid="sub-action-invoice-now"><Send className="w-4 h-4 mr-1" /> Facturar ahora</Button>
                  <Button size="sm" variant="outline" onClick={() => doAction('cancel-end')} className="text-amber-600 border-amber-200" data-testid="sub-action-cancel-end"><Clock className="w-4 h-4 mr-1" /> Cancelar al final</Button>
                  <Button size="sm" variant="outline" onClick={() => doAction('cancel-now')} className="text-rose-600 border-rose-200" data-testid="sub-action-cancel-now"><X className="w-4 h-4 mr-1" /> Cancelar ya</Button>
                </>
              )}
              {sub.status === 'paused' && (
                <Button size="sm" onClick={() => doAction('resume')} className="bg-emerald-600 text-white" data-testid="sub-action-resume"><Play className="w-4 h-4 mr-1" /> Reanudar</Button>
              )}
              {sub.status === 'cancelled' && (
                <Button size="sm" onClick={() => doAction('reactivate')} className="bg-emerald-600 text-white" data-testid="sub-action-reactivate"><RefreshCw className="w-4 h-4 mr-1" /> Reactivar</Button>
              )}
              {sub.status === 'trial' && (
                <Button size="sm" onClick={() => doAction('invoice-now')} variant="outline"><Send className="w-4 h-4 mr-1" /> Finalizar trial y facturar</Button>
              )}
            </div>
          )}

          {tab === 'invoices' && (
            <div>
              {invoices.length === 0 ? <p className="text-sm text-stone-400 text-center py-6">Sin facturas generadas</p> : (
                <table className="w-full text-sm">
                  <thead className="text-xs text-stone-500"><tr><th className="text-left">#</th><th className="text-left">Fecha</th><th className="text-right">Total</th><th className="text-center">Estado</th></tr></thead>
                  <tbody>
                    {invoices.map((inv) => (
                      <tr key={inv.id} className="border-t border-stone-100">
                        <td className="py-2 font-mono">{inv.invoice_number}</td>
                        <td className="py-2">{fmtDate(inv.issue_date)}</td>
                        <td className="py-2 text-right font-bold">{fmt(inv.total)}</td>
                        <td className="py-2 text-center"><Badge>{inv.status}</Badge></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {tab === 'events' && (
            <div className="space-y-1 max-h-[300px] overflow-y-auto">
              {events.map((e) => (
                <div key={e.id} className="flex justify-between text-xs py-1.5 border-b border-stone-100 last:border-0">
                  <span className="font-medium text-stone-700">{e.type}</span>
                  <span className="text-stone-400">{new Date(e.created_at).toLocaleString('es-ES')}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const Field = ({ label, value }) => (
  <div>
    <p className="text-[10px] uppercase font-semibold text-stone-500">{label}</p>
    <p className="text-sm font-medium text-stone-800 mt-0.5">{value || '—'}</p>
  </div>
);

export default SubscriptionsPage;
